'use client';

import { Component, useEffect, useRef, useState } from 'react';
import { logError } from '@zg-rentals/log-error';
import type { ReactNode } from 'react';
import { Button, Heading, Spinner, Tabs } from '@zillow/constellation-10';
import { AccessibleTabsRoot } from '@/src/components/features/property/ui/AccessibleTabsRoot';
import { ReviewsSourceIcon, type ReviewSourceId } from './ReviewsSourceIcon';
import * as styles from './reviews.styles';

type ReadyReviewPanel = {
  status: 'ready';
  render: () => ReactNode;
};

type PendingReviewPanel = {
  status: 'loading';
  message?: string;
};

type EmptyReviewPanel = {
  status: 'empty' | 'unavailable';
  message?: string;
};

type FailedReviewPanel = {
  status: 'error';
  message?: string;
  onRetry?: () => void;
};

export type ReviewPanel = ReadyReviewPanel | PendingReviewPanel | EmptyReviewPanel | FailedReviewPanel;

export type ReviewSourceConfig = {
  id: ReviewSourceId;
  label: string;
  instanceKey?: string;
  available?: boolean;
  panel: ReviewPanel;
};

type ReviewsShellProps = {
  id: string;
  sources: Array<ReviewSourceConfig>;
  title?: string;
  initialSource?: ReviewSourceId;
  enabled?: boolean;
  onProviderChange?: (source: ReviewSourceId) => void;
};

function ProviderState({ panel }: { panel: Exclude<ReviewPanel, ReadyReviewPanel> }) {
  if (panel.status === 'loading') {
    const message = panel.message ?? 'Loading reviews';
    return (
      // `output` cannot contain the block-level status content rendered here.
      // eslint-disable-next-line jsx-a11y/prefer-tag-over-role
      <div className={styles.providerState} role="status" aria-live="polite" aria-busy="true">
        <Spinner title={message} />
        <p className={styles.providerStateMessage}>{message}</p>
      </div>
    );
  }

  if (panel.status === 'error') {
    return (
      <div className={styles.providerState} role="alert">
        <p className={styles.providerStateMessage}>{panel.message ?? 'Reviews could not be loaded.'}</p>
        {panel.onRetry && (
          <Button size="sm" tone="brand" emphasis="outlined" onClick={panel.onRetry}>
            Try again
          </Button>
        )}
      </div>
    );
  }

  const message =
    panel.message ??
    (panel.status === 'empty' ? 'There are no reviews to show yet.' : 'This review source is temporarily unavailable.');
  return (
    <div className={styles.providerState}>
      <p className={styles.providerStateMessage}>{message}</p>
    </div>
  );
}

class ProviderBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error: Error) {
    logError({ error, errorType: 'ApartmentReviewsPanel' });
  }
  render() {
    return this.state.failed ? (
      <ProviderState panel={{ status: 'error', onRetry: () => this.setState({ failed: false }) }} />
    ) : (
      this.props.children
    );
  }
}

function ReadyPanel({ render }: ReadyReviewPanel) {
  return render();
}

function SourcePanel({ source, selected, single }: { source: ReviewSourceConfig; selected: boolean; single: boolean }) {
  const element = useRef<HTMLDivElement>(null);
  const [activated, setActivated] = useState(false);
  useEffect(() => {
    if (source.id !== 'google' || !selected || activated) return;
    if (!single || !('IntersectionObserver' in window)) {
      setActivated(true);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setActivated(true);
        observer.disconnect();
      }
    });
    if (element.current) observer.observe(element.current);
    return () => observer.disconnect();
  }, [source.id, selected, single, activated]);
  const mount = source.id === 'google' ? activated : selected;
  return (
    <div ref={element}>
      {mount ? (
        <ProviderBoundary>
          {source.panel.status === 'ready' ? <ReadyPanel {...source.panel} /> : <ProviderState panel={source.panel} />}
        </ProviderBoundary>
      ) : selected ? (
        <ProviderState panel={{ status: 'loading' }} />
      ) : null}
    </div>
  );
}

export function ReviewsShell({
  id,
  sources,
  title = 'What people say',
  initialSource,
  enabled = true,
  onProviderChange,
}: ReviewsShellProps) {
  const availableSources = sources.filter((source) => source.available !== false);
  const firstSource =
    availableSources.find((source) => source.id === initialSource) ??
    availableSources.find((source) => source.id === 'web') ??
    availableSources.find((source) => source.id === 'reddit') ??
    availableSources[0];
  const [selectedSourceId, setSelectedSourceId] = useState<ReviewSourceId | undefined>(firstSource?.id);
  const selectedSource = availableSources.find((source) => source.id === selectedSourceId) ?? firstSource;
  useEffect(() => {
    setSelectedSourceId(selectedSource?.id);
  }, [selectedSource?.id]);
  if (!enabled || !selectedSource) return null;
  const single = availableSources.length === 1;

  return (
    <section className={styles.shell} id={id} aria-labelledby={`${id}-title`}>
      <Heading level={2} textStyle="heading-xs" id={`${id}-title`} tabIndex={-1} className={styles.shellTitle}>
        {title}
      </Heading>
      {!single && (
        <AccessibleTabsRoot
          selected={selectedSource.id}
          onSelectedChange={(value) => {
            const source = availableSources.find((item) => item.id === value);
            if (source && source.id !== selectedSource.id) {
              setSelectedSourceId(source.id);
              onProviderChange?.(source.id);
            }
          }}
          ariaLabel="Review sources"
          css={styles.tabRoot}
        >
          <Tabs.List role="presentation">
            {availableSources.map((source) => (
              <Tabs.Tab
                key={source.id}
                value={source.id}
                id={`${id}-${source.id}-tab`}
                aria-controls={`${id}-${source.id}-panel`}
              >
                <span className={styles.tabLabel}>
                  <ReviewsSourceIcon source={source.id} />
                  {source.label}
                </span>
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </AccessibleTabsRoot>
      )}
      {availableSources.map((source) => (
        <div
          key={source.id}
          className={styles.panel}
          id={`${id}-${source.id}-panel`}
          role={single ? undefined : 'tabpanel'}
          aria-labelledby={single ? undefined : `${id}-${source.id}-tab`}
          hidden={source.id !== selectedSource.id}
        >
          <SourcePanel
            key={source.instanceKey ?? source.id}
            source={source}
            selected={source.id === selectedSource.id}
            single={single}
          />
        </div>
      ))}
    </section>
  );
}
